/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { redirect } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';

// APU & Types
import GET_ME from '@features/Account/api/getMe';
import { GetMeQuery, GetMeQueryVariables } from '@app-types/blackroom-api';
import { LoaderFunctionArgs } from 'react-router-dom';

/* -------------------------------------------------------------------------- */
/*                                    Hook                                    */
/* -------------------------------------------------------------------------- */

export function useVerifyAuth() {
  /* ********************************** HOOKS ********************************* */

  // API
  const apolloClient = useApolloClient();

  // Auth
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();

  /* ***************************** IMPLEMENTATION ***************************** */
  /**
   * Loader function for React Router. Verifies token validity
   * prior to each route navigation
   * (having a token doesn't necessairly mean you can do whatever you want in the app)
   */
  const verifyAuth = async (args: LoaderFunctionArgs) => {
    if (isLoading) {
      return null;
    }
    if (isAuthenticated) {
      let token: string | undefined;
      try {
        token = await getAccessTokenSilently();
      } catch {
        return redirect('/home');
      }
      // Invalid token locally, no need to query the server
      if (!token) {
        return redirect('/home');
      }
      // Token shape looks good, but still verify on server
      try {
        const res = await apolloClient.query<GetMeQuery, GetMeQueryVariables>({
          query: GET_ME,
          fetchPolicy: 'network-only',
        });
        if (!res.data.me) {
          return redirect('/home');
        }
        return null;
      } catch {
        return redirect('/home');
      }
    }
    return redirect('/home');
  };

  return { verifyAuth };
}
